import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ImageInput } from "@/components/ImageInput";

const FIELDS = [
  { name: "name", label: "Brand Name", type: "text" },
  { name: "tagline", label: "Tagline", type: "text" },
  { name: "logo_url", label: "Logo", type: "image" },
  { name: "hero_title", label: "Homepage Hero Title", type: "text" },
  { name: "hero_subtitle", label: "Homepage Hero Subtitle", type: "textarea" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "whatsapp", label: "WhatsApp Number", type: "text" },
  { name: "email", label: "Email", type: "text" },
  { name: "address", label: "Office Address", type: "text" },
  { name: "instagram", label: "Instagram URL", type: "text" },
  { name: "site_url", label: "Website URL", type: "text" },
  { name: "primary_color", label: "Primary / Gold Color (hex)", type: "text" },
  { name: "accent_color", label: "Accent Color (hex)", type: "text" },
  { name: "dark_color", label: "Dark Background Color (hex)", type: "text" },
];

const GROWTH_FIELDS = [
  { name: "theme", label: "Public site theme (dark / light)", type: "text", placeholder: "dark" },
  { name: "followup_enabled", label: "Enable lead follow-up nudges", type: "checkbox" },
  { name: "followup_email", label: "Send nudges by Email (Resend)", type: "checkbox" },
  { name: "followup_whatsapp", label: "Send nudges by WhatsApp (needs a Docstate number)", type: "checkbox" },
  { name: "followup_first_hours", label: "1st nudge after (hours of silence)", type: "number", placeholder: "24" },
  { name: "followup_second_hours", label: "2nd nudge after (hours of silence)", type: "number", placeholder: "72" },
  { name: "followup_max", label: "Max nudges per lead", type: "number", placeholder: "2" },
  { name: "lead_routing", label: "Lead routing mode (smart / roundrobin / central)", type: "text", placeholder: "smart" },
  { name: "lead_central_number", label: "Central sales WhatsApp (used when routing = central)", type: "text" },
  { name: "leadad_form_ids", label: "Meta Lead-Ad Form IDs for Docstate (comma-separated)", type: "text" },
  { name: "leadad_page_ids", label: "Meta Page IDs for Docstate (comma-separated, optional)", type: "text" },
  { name: "meta_leadads_token", label: "Meta Page Access Token for Lead Ads (optional)", type: "text" },
];

export const ReSettingsTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/realestate/settings").then((r) => setForm(r.data || {})).catch(() => setForm({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/realestate/settings", form);
      setForm(data);
      toast.success("Docstate settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (form === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  const renderField = (fd) => {
    if (fd.type === "checkbox") {
      return (
        <label key={fd.name} className="flex items-center gap-3 rounded-lg border border-[#E8E2D9] bg-white px-4 py-3">
          <input type="checkbox" checked={form[fd.name] ?? (fd.name.startsWith("followup") ? true : false)} onChange={(e) => setForm({ ...form, [fd.name]: e.target.checked })} data-testid={`re-settings-${fd.name}`} className="h-4 w-4 accent-[#B8912E]" />
          <span className="text-sm text-[#1A1615]">{fd.label}</span>
        </label>
      );
    }
    return (
      <div key={fd.name}>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">{fd.label}</label>
        {fd.type === "textarea" ? (
          <textarea rows={3} value={form[fd.name] || ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`re-settings-${fd.name}`} className="input-lux resize-none" />
        ) : fd.type === "image" ? (
          <ImageInput value={form[fd.name] || ""} onChange={(v) => setForm({ ...form, [fd.name]: v })} testid={`re-settings-${fd.name}`} />
        ) : (
          <input type={fd.type === "number" ? "number" : "text"} placeholder={fd.placeholder || ""} value={form[fd.name] ?? ""} onChange={(e) => setForm({ ...form, [fd.name]: fd.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} data-testid={`re-settings-${fd.name}`} className="input-lux" />
        )}
      </div>
    );
  };

  return (
    <div className="max-w-2xl" data-testid="re-settings-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Docstate — Brand Settings</h2>
      <p className="mt-1 text-xs text-[#91837C]">Branding and contact details for the real-estate portal.</p>
      <div className="mt-6 space-y-4">
        {FIELDS.map(renderField)}
      </div>

      <h3 className="mt-10 font-serif text-xl font-semibold text-[#1A1615]">Lead Follow-ups & Ads</h3>
      <p className="mt-1 text-xs text-[#91837C]">How Sara nudges quiet leads and where Facebook/Instagram lead-ad submissions are routed.</p>
      <div className="mt-6 space-y-4">
        {GROWTH_FIELDS.map(renderField)}
        <button onClick={save} disabled={saving} data-testid="re-settings-save-button" className="btn-gold w-full px-6 py-3.5 text-sm disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};
